import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_PAYMENT_POLL_ATTEMPTS = 5;

export const useAuth = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialized = useRef(false);

  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/profile/me`, { withCredentials: true });
      setUser(response.data);
    } catch (error) {
      // Not authenticated - stay on login screen
    } finally {
      setLoading(false);
    }
  }, []);

  const processSessionId = useCallback(async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, {}, {
        headers: { 'X-Session-ID': sessionId },
        withCredentials: true,
      });
      if (response.data.success) {
        await fetchUserProfile();
        window.location.hash = '';
        toast.success('Successfully logged in!');
      }
    } catch (error) {
      console.error('Session processing error:', error);
      toast.error('Authentication failed');
      setLoading(false);
    }
  }, [fetchUserProfile]);

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= MAX_PAYMENT_POLL_ATTEMPTS) {
      toast.error('Payment verification timed out');
      return;
    }
    try {
      const response = await axios.get(`${API}/payment/status/${sessionId}`, { withCredentials: true });
      if (response.data.payment_status === 'paid') {
        toast.success('Payment successful! You now have premium access.');
        await fetchUserProfile();
        return;
      }
      if (response.data.status === 'expired') {
        toast.error('Payment session expired');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Payment status check error:', error);
      toast.error('Error checking payment status');
    }
  }, [fetchUserProfile]);

  const handleLogout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      toast.success('Logged out successfully');
    } catch (error) {
      console.error('Logout error:', error);
    }
  }, []);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      processSessionId(sessionId);
    } else {
      fetchUserProfile();
    }

    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success' && params.get('session_id')) {
      pollPaymentStatus(params.get('session_id'));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchUserProfile, processSessionId, pollPaymentStatus]);

  return { user, loading, handleLogout, fetchUserProfile };
};
